import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import ChatComponent from '@/components/chat';
import GameComponent from '@/components/game';
import IFrameGameComponent from '@/components/iframe-game';
import ArcadeGameComponent from '@/components/arcade-game';
import GameWinnersComponent from '@/components/game-winners';
import UserSettingsComponent from '@/components/user-settings';
import PlayerNotesComponent from '@/components/player-notes';
import { IModal } from '@/plugins/modal-manager';
import { IModalEventNotification, ModalEventType } from '@/components/home/_components/tombola-modal/_domain/modal-events';
import TombolaBaseComponent from '@/components/base/base.component';
import { UserPreferences, WindowType, IWindowLocationPreference } from '@/app-modules/store/modules/user-preferences';
import { IModalLaunchOptions, IChatModalLaunchOptions } from '@/plugins/modal-manager/models';
import TombolaMinimisedModal from '@/components/home/_components/minimised-modal';
import { UserSettingsUpdatedEventName } from '@/components/user-settings/_domain/models/events';
import ChatContextInteractionComponent from '@/components/chat-context-interaction';
import { ChatContextInteractionSubmit } from '@/components/chat-context-interaction/models';
import { IFrameGameClickedEventName } from '@/components/iframe-game/_domain/models/events';

const DEFAULT_MODAL_MIN_WIDTH_PIXELS = 310;
const DEFAULT_MODAL_HEIGHT_PIXELS = 585;
const DEFAULT_CHAT_MODAL_MIN_WIDTH_PIXELS = 215;
const DEFAULT_CHAT_MODAL_HEIGHT_PIXELS = 500;

@Component({
  name: 'TombolaModalComponent',
  components: {
    Chat: ChatComponent,
    Game: GameComponent,
    IFrameGame: IFrameGameComponent,
    ArcadeGame: ArcadeGameComponent,
    GameWinners: GameWinnersComponent,
    UserSettings: UserSettingsComponent,
    PlayerNotes: PlayerNotesComponent,
    ChatContextInteraction: ChatContextInteractionComponent
  },
})

export default class TombolaModalComponent extends TombolaBaseComponent implements IModal {
  @Prop() public modalType: WindowType;
  @Prop() public modalTitle: string;
  @Prop() public modalTooltip: string;
  @Prop() public modalProps: IModalLaunchOptions;
  @Prop({ default: ''}) public container: string;
  public componentKey: number = 0;
  public stylingClasses: string = '';
  public minModalWidth: number = 300;
  public modalHeight: number = 500;
  public windowEventCallback: IModalEventNotification;
  public isActive: boolean = false;
  public isGrabbed: boolean = false;
  public modalGuid: string = '';
  public minimisedModal: TombolaMinimisedModal = null;
  public isMinimised: boolean = false;
  public modalTitleSuffix: string = '';

  public get modalTitleText(): string {
    return `${this.modalTitle || this.modalType.toString()} ${this.modalTitleSuffix}`.trim();
  }

  public get modalTooltipText(): string {
    return this.modalTooltip;
  }

  public get modalId(): string {
    return `modal_${this._uid}`;
  }

  @Watch('modalTitleSuffix')
  private watchModalTitleSuffix(newmodalTitleSuffix: string) {
    if (this.minimisedModal !== null) {
      (this.minimisedModal as any).modalTitle = this.modalTitleText;
    }
  }

  private beforeMount() {
    this.setMinWidth();
    this.setHeight();
  }

  private mounted() {
    this.setupModalGuid();
    this.$eventbus.$on(UserSettingsUpdatedEventName, this.forceComponentUpdate);
    this.$eventbus.$on(ChatContextInteractionSubmit, this.destroyModalByGuid);

    Vue.nextTick(() => {
      if (!this.isRegisteredWithModalManager()) {
        this.show();
        this.registerWithModalManager();
        this.populateStylingClassess();
      }
      else {
        this.bringToFront();
      }
    });

    if (this.modalType === 'IFrameGame') {
      this.$eventbus.$on(IFrameGameClickedEventName, this.onIFrameGameClicked);
    }
  }

  private beforeDestroy() {
    this.$eventbus.$off(UserSettingsUpdatedEventName, this.forceComponentUpdate);

    if (this.modalType === 'IFrameGame') {
      this.$eventbus.$off(IFrameGameClickedEventName, this.onIFrameGameClicked);
    }
  }

  private setupModalGuid() {
    this.modalGuid = this.modalProps.modalGuid;
  }

  private forceComponentUpdate() {
    Vue.nextTick(() => {
      if (this.modalType === WindowType.Chat) {
        const chatModalLaunchOptions = this.modalProps as IChatModalLaunchOptions;
        chatModalLaunchOptions.chatBackgroundColour = this.$tombolaStore.Get(UserPreferences).chatBackgroundColour;
      }
      this.componentKey += 1;
    });
  }

  private isRegisteredWithModalManager(): boolean {
    return this.$modalManager.isModalRegistered(this.modalGuid);
  }

  private registerWithModalManager() {
    this.$modalManager.registerModal(this);
    this.$modal.show(this.modalId);
  }

  private populateStylingClassess() {
    const userCountry = this.$tombolaStore.Get(UserPreferences).country;
    const isBoldFont = this.$tombolaStore.Get(UserPreferences).boldFont;
    let stylingClasses = `${userCountry} ${this.modalType.toString()} `;

    const gameName = this.modalProps.gameName;
    if (gameName) {
      stylingClasses += gameName.replace(' ',  '-') + ' ';
    }

    const clubName = this.modalProps.clubName;
    if (clubName) {
      stylingClasses += clubName.replace(' ',  '-') + ' ';
    }

    if (isBoldFont && this.modalType !== WindowType.UserSettings) {
      stylingClasses += 'font-bold ';
    }

    this.stylingClasses = stylingClasses.toLowerCase();
  }

  private getModalClasses() {
    const classes: { [key: string]: boolean } = {};
    classes['tombola-modal-active'] = this.isActive;
    classes['tombola-modal-minimised'] = this.isMinimised;
    classes[this.stylingClasses] = true;
    return classes;
  }

  private destroySelf() {
    this.$modalManager.deregisterModal(this);
    this.$destroy();
    this.$el.remove();
  }

  private destroyModalByGuid(modalGuid: string) {
    const modal = this.$modalManager.getModal(modalGuid);
    if (modal !== undefined) {
      this.$modalManager.deregisterModal(modal);
      modal.$destroy();
      modal.$el.remove();
    }
  }

  private registerClick() {
    this.bringToFront();
  }

  private setMinWidth() {
    this.minModalWidth = this.modalType === WindowType.Chat ? DEFAULT_CHAT_MODAL_MIN_WIDTH_PIXELS : DEFAULT_MODAL_MIN_WIDTH_PIXELS;
  }

  private setHeight() {
    this.modalHeight = this.modalType === WindowType.Chat ? DEFAULT_CHAT_MODAL_HEIGHT_PIXELS : DEFAULT_MODAL_HEIGHT_PIXELS;
  }

  private setGrabbedState(state: boolean) {
    this.isGrabbed = state;

    this.handleIFrameInteractivity(this.isGrabbed);
  }

  private onResize(): void {
    this.handleIFrameInteractivity(true);
  }

  private resizeStop(): void {
    this.handleIFrameInteractivity(false);
    this.handleModalDraggedOrResized();
  }

  private handleModalDraggedOrResized() {
    this.$modalManager.updateUserWindowPreferenceForModal(this.modalId);
  }

  private show(): void {
    this.$modal.show(this.modalId);
  }

  private bringToFront(): void {
    this.$modalManager.bringToFront(this.modalGuid);
  }

  private handleIFrameInteractivity(shouldDisable: boolean): void {
    if (this.modalType !== 'IFrameGame') {
      return;
    }

    const iFrameElements = document.getElementsByClassName('game-iframe');

    for (const key in iFrameElements) {
      if (!isNaN(parseInt(key, 10))) {
        const element = iFrameElements[key] as HTMLElement;
        element.style.pointerEvents = shouldDisable ? 'none' : 'auto';
      }
    }
  }

  private onIFrameGameClicked(clickedModalGuid: string): void {
    if (this.modalGuid === clickedModalGuid) {
      this.registerClick();
    }
  }

  public moveTo(newPositionInPixels: { left: number, top: number }): void {
    this.$modal.moveTo(this.modalId, newPositionInPixels);
  }

  public resize(newSizeInPixels: { width: number, height: number }): void {
    this.$modal.resize(this.modalId, newSizeInPixels);
  }

  public getLocationAndDimensionsInPixels(): IWindowLocationPreference {
    const modalComponent = this.$refs.modal as any;
    if (modalComponent) {
      const position = modalComponent.position;
      const width = modalComponent.trueModalWidth;
      const height = modalComponent.trueModalHeight;
      return {
        top: position.top,
        left: position.left,
        width,
        height
      };
    }
    return { top: 0, left: 0, width: 0, height: 0 };
  }

  public minimise(): void {
    if (!this.isMinimised) {
      this.minimisedModal = this.windowEventCallback(this, ModalEventType.Minimise);
      this.isMinimised = true;
    }
  }

  public restore(): void {
    if (this.isMinimised && this.minimisedModal) {
      this.minimisedModal.$destroy();
      this.minimisedModal.$el.remove();
      this.minimisedModal = null;
      this.isMinimised = false;
    }
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/home/_components/tombola-modal/tombola-modal.component.ts